import { useMemo, useCallback } from 'react';
import {
  orderStatusEnum,
  orderStatusesSelfInsuranceEditAdditionalItems,
  orderStatusesSelfInsuranceEditInsuranceItemsBlock,
  SELF_INSURANCE_INVOICE_SUMMARY_HIDDEN_STATUSES,
  SELF_INSURANCE_PROVIDER_KEY,
} from 'modules/orders/constants';
import { getInvoiceRowCheckboxKey } from './invoiceCheckboxZeroPrice';
import { TABLE_VIEWS } from './helper';

export function normalizeOrderStateTypeId(raw) {
  if (raw == null || String(raw).trim() === '') return raw;
  return Number(raw);
}

function getSelectedInsuranceRowsForApproval({
  dispensedOrderItems,
  productsAlternatives,
  checkedItemToBeConfirmed,
}) {
  const rows = Array.isArray(dispensedOrderItems) ? dispensedOrderItems : [];
  const insuranceRows = rows.filter(item => item.prescriptionKey != null);
  if (!insuranceRows.length) return [];

  const checkedSet = new Set(
    Array.isArray(checkedItemToBeConfirmed) ? checkedItemToBeConfirmed : [],
  );
  const alternativesList = Array.isArray(productsAlternatives) ? productsAlternatives : [];

  return insuranceRows.filter(row => {
    const rowKey = getInvoiceRowCheckboxKey(row);
    const hasSelectedAlternative = alternativesList.some(group => {
      if (!Array.isArray(group?.alternatives) || group.alternatives.length === 0) return false;
      const keyMatches =
        group.itemProductKey === row.orderItemKey || group.itemProductKey === row.serviceKey;
      const shapeMatches = Number(group.itemShapeId) === Number(row.productShapeId);
      return keyMatches && shapeMatches;
    });
    return checkedSet.has(rowKey) || hasSelectedAlternative;
  });
}

/**
 * Maps insurance / e-prescription invoice rows to POST .../insurance-approval-request body items.
 */
export function buildInsuranceApprovalOrderInsuranceItems(
  insuranceInvoiceRows,
  productsAlternatives,
) {
  const altList = Array.isArray(productsAlternatives) ? productsAlternatives : [];
  return insuranceInvoiceRows.map(row => {
    const orderLineKey = row.orderItemKey;
    const altGroup = altList.find(
      pa =>
        (pa.itemProductKey === orderLineKey || pa.itemProductKey === row.serviceKey) &&
        Number(pa.itemShapeId) === Number(row.productShapeId),
    );
    const alt = altGroup?.alternatives?.[0];
    const entry = {
      itemKey: orderLineKey,
      quantity: Number(row.quantity) || 0,
    };
    if (alt) {
      entry.alternative = {
        productShapeId: Number(alt.itemShapeId) || 0,
        productShapeTypeId: Number(alt.productShapeTypeId) || 0,
        productKey: alt.itemProductKey,
        quantity: Number(alt.itemQuantity) || 0,
        price: Number(alt.itemPrice) || 0,
      };
    }
    return entry;
  });
}

/**
 * Self-insurance: normalized order state, feature flags, and request-approval action.
 */
export function useSelfInsuranceOrder({
  orderDetails,
  orderId,
  dispatch,
  submitInsuranceApprovalRequest,
  reloadOrderDetailsPage,
  dispensedOrderItems,
  productsAlternatives,
  checkedItemToBeConfirmed,
}) {
  const details = orderDetails?.details;
  const orderStateTypeId = normalizeOrderStateTypeId(details?.orderStateTypeId);
  const patientInsurance = details?.patientInsurance;

  const isSelfInsuranceOrder =
    patientInsurance?.insuranceProviderKey === SELF_INSURANCE_PROVIDER_KEY;

  const isSelfInsuranceAcceptedLayout =
    isSelfInsuranceOrder && orderStateTypeId === orderStatusEnum.Accepted;

  const selectedInsuranceRowsForApproval = useMemo(
    () =>
      getSelectedInsuranceRowsForApproval({
        dispensedOrderItems,
        productsAlternatives,
        checkedItemToBeConfirmed,
      }),
    [dispensedOrderItems, productsAlternatives, checkedItemToBeConfirmed],
  );

  const selfInsuranceInsuranceLineCount = useMemo(() => {
    const list = Array.isArray(dispensedOrderItems) ? dispensedOrderItems : [];
    return list.filter(item => item.prescriptionKey != null).length;
  }, [dispensedOrderItems]);

  const canRequestSelfInsuranceApproval = selectedInsuranceRowsForApproval.length > 0;

  const requestSelfInsuranceApproval = useCallback(() => {
    const orderKey = details?.key ?? orderId;
    if (!orderKey) return;
    if (!canRequestSelfInsuranceApproval) return;
    const orderInsuranceItems = buildInsuranceApprovalOrderInsuranceItems(
      selectedInsuranceRowsForApproval,
      productsAlternatives,
    );
    dispatch(
      submitInsuranceApprovalRequest({
        orderKey,
        data: { orderInsuranceItems },
        callbackAction: reloadOrderDetailsPage,
      }),
    );
  }, [
    details?.key,
    orderId,
    dispatch,
    submitInsuranceApprovalRequest,
    reloadOrderDetailsPage,
    canRequestSelfInsuranceApproval,
    selectedInsuranceRowsForApproval,
    productsAlternatives,
  ]);

  return {
    orderStateTypeId,
    isSelfInsuranceOrder,
    isSelfInsuranceAcceptedLayout,
    selfInsuranceInsuranceLineCount,
    canRequestSelfInsuranceApproval,
    requestSelfInsuranceApproval,
  };
}

/**
 * Self-insurance invoice: split rows, editability, summary visibility, table-mode sync.
 */
export function useSelfInsuranceInvoice({
  orderDetails,
  dispensedOrderItems,
  orderStatusToEnableEditInvoice,
  insuranceBlockTableMode,
  additionalBlockTableMode,
}) {
  const orderData = orderDetails.details;
  const orderStatus = normalizeOrderStateTypeId(orderData.orderStateTypeId);

  const enableEditInvoice = orderStatusToEnableEditInvoice.includes(orderStatus);

  const isSelfInsuranceOrder =
    orderData.patientInsurance?.insuranceProviderKey === SELF_INSURANCE_PROVIDER_KEY;

  const isDispensedOrderItemsArray = Array.isArray(dispensedOrderItems);
  const hasDispensedItems = isDispensedOrderItemsArray && dispensedOrderItems.length > 0;

  const { insuranceInvoiceRows, additionalInvoiceRows } = useMemo(() => {
    const list = Array.isArray(dispensedOrderItems) ? dispensedOrderItems : [];
    const withRowIndex = list.map((item, idx) => ({
      ...item,
      invoiceRowIndex: idx,
    }));
    return {
      insuranceInvoiceRows: withRowIndex.filter(item => item.prescriptionKey != null),
      additionalInvoiceRows: withRowIndex.filter(item => item.prescriptionKey == null),
    };
  }, [dispensedOrderItems]);

  /** Viewed: e-prescription list is in InsuranceOrderSection; invoice shows additional lines only. */
  const showSelfInsuranceViewedAdditionalOnly =
    isSelfInsuranceOrder && orderStatus === orderStatusEnum.Viewed;

  const showInsuranceBlock =
    isSelfInsuranceOrder &&
    hasDispensedItems &&
    !showSelfInsuranceViewedAdditionalOnly &&
    insuranceInvoiceRows.length > 0;
  const showAdditionalBlock =
    isSelfInsuranceOrder && hasDispensedItems && additionalInvoiceRows.length > 0;
  const enableEditInsuranceBlock =
    orderStatusesSelfInsuranceEditInsuranceItemsBlock.includes(orderStatus);

  const enableEditAdditionalBlock =
    orderStatusesSelfInsuranceEditAdditionalItems.includes(orderStatus);

  const enableInsuranceAlternativesTab = enableEditInsuranceBlock;
  const enableAdditionalAlternativesTab = enableEditAdditionalBlock;

  const insuranceTableViewMode = enableInsuranceAlternativesTab
    ? insuranceBlockTableMode
    : TABLE_VIEWS.ORDER_PREPARATION;
  const additionalTableViewMode = enableAdditionalAlternativesTab
    ? additionalBlockTableMode
    : TABLE_VIEWS.ORDER_PREPARATION;

  // Self-insurance: search/add should follow additional-items editability only.
  const enableSearchItemsInOrderPreparation = enableEditAdditionalBlock;
  const showSelfInsuranceInvoiceSummaryAtBottom =
    !SELF_INSURANCE_INVOICE_SUMMARY_HIDDEN_STATUSES.includes(orderStatus);

  return {
    orderStatus,
    enableEditInvoice,
    insuranceInvoiceRows,
    additionalInvoiceRows,
    isDispensedOrderItemsArray,
    isSelfInsuranceOrder,
    showInsuranceBlock,
    showAdditionalBlock,
    showSelfInsuranceViewedAdditionalOnly,
    enableEditInsuranceBlock,
    enableEditAdditionalBlock,
    enableInsuranceAlternativesTab,
    enableAdditionalAlternativesTab,
    insuranceTableViewMode,
    additionalTableViewMode,
    enableSearchItemsInOrderPreparation,
    showSelfInsuranceInvoiceSummaryAtBottom,
  };
}
